<template>
  <div class="status_block">
    <div class="source_network_label">{{$t('message.source_network')}}</div>
    <div class="network_tabs">
      <button class="network_tab" :class="{ active: currentTab === 'bep20' }" @click="onTabChange('bep20')">BEP-20</button>
      <button class="network_tab" :class="{ active: currentTab === 'erc20' }" @click="onTabChange('erc20')">ERC-20</button>
      <button class="network_tab" :class="{ active: currentTab === 'native' }" @click="onTabChange('native')">{{$t('message.main_net_token')}}</button>
    </div>

    <div class="table_wrap">
      <div class="native_table">
        <div class="table_head">
          <div class="col_time">{{$t('message.col_time')}}</div>
          <div class="col_chain">{{$t('message.col_target_format')}}</div>
          <div class="col_addr">{{$t('message.col_target_account')}}</div>
          <div class="col_amount">{{$t('message.col_amount')}}</div>
          <div class="col_status">{{$t('message.col_status')}}</div>
        </div>
        <div class="table_line"></div>

        <div v-if="isLoading" class="table_empty">{{$t('message.loading')}}</div>
        <div v-else-if="rows.length === 0" class="table_empty">{{$t('message.no_data')}}</div>
        <div v-else v-for="(row, idx) in rows" :key="idx" class="table_row">
          <div class="col_time row_muted">{{ row.time }}<span v-if="row.chainLabel" class="chain_badge chain_badge_inline">{{ row.chainLabel }}</span></div>
          <div class="col_chain"><span v-if="row.chainLabel" class="chain_badge">{{ row.chainLabel }}</span></div>
          <div class="col_addr row_addr">{{ row.address }}</div>
          <div class="col_amount row_muted">{{ row.amount }}</div>
          <div class="col_status">
            <div class="status_row">
              <span :class="row.done ? 'status_done' : 'status_pending'">{{ row.status }}</span>
              <a v-if="row.done && row.txHash" class="status_hash_link" :href="getTxLink(row)" target="_blank" rel="noopener noreferrer">{{ row.txHashShort }}</a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="pagination_bar">
      <div class="page_hint">{{ nextUpdateHint }}</div>
      <div v-if="currentPage > 1 || hasMore" class="page_controls">
        <button class="page_btn" :disabled="currentPage <= 1" @click="goToPage(currentPage - 1)">&lt;</button>
        <button class="page_btn" :disabled="!hasMore" @click="goToPage(currentPage + 1)">&gt;</button>
      </div>
    </div>
  </div>
</template>

<script>
import webAPI from '../../common/v2/webService'
import utils from '../../common/v2/utils'

// source_token 值與 tab key 對應
const TAB_TO_SOURCE = {
  bep20: 'bep20',
  erc20: 'erc20',
  native: 'cos'
}

const PAGE_SIZE = 10

export default {
  name: 'conversionStatusView',
  data () {
    return {
      currentTab: 'bep20',
      currentPage: 1,
      until: null,
      hasMore: false,
      rows: [],
      isLoading: false,
      refreshIntervalSec: 300,
      secondsUntilRefresh: 300,
      countdownTimer: null
    }
  },
  computed: {
    nextUpdateHint () {
      const mm = String(Math.floor(this.secondsUntilRefresh / 60)).padStart(2, '0')
      const ss = String(this.secondsUntilRefresh % 60).padStart(2, '0')
      return this.$t('message.next_update_hint', { time: `${mm}:${ss}` })
    }
  },
  async mounted () {
    await this.fetchData()
    this.startCountdown()
  },
  beforeDestroy () {
    this.stopCountdown()
  },
  methods: {
    formatAmount (value) {
      const num = parseFloat(parseFloat(value).toFixed(2))
      const locale = this.$i18n ? this.$i18n.locale : 'en'
      const localeMap = { 'zh': 'zh-CN', 'zh-TW': 'zh-TW', 'ko': 'ko-KR', 'ja': 'ja-JP', 'vi': 'vi-VN', 'ru': 'ru-RU', 'pt-br': 'pt-BR' }
      return num.toLocaleString(localeMap[locale] || locale, { minimumFractionDigits: 0, maximumFractionDigits: 2 })
    },
    async onTabChange (tab) {
      this.currentTab = tab
      this.currentPage = 1
      this.until = null
      this.hasMore = false
      await this.fetchData()
    },
    async goToPage (page) {
      if (this.isLoading) return
      if (page < 1) return
      if (page > this.currentPage && !this.hasMore) return
      this.currentPage = page
      if (page === 1) {
        this.until = null
      }
      await this.fetchData()
    },
    async fetchData () {
      this.isLoading = true
      const sourceToken = TAB_TO_SOURCE[this.currentTab]
      const opts = { limit: PAGE_SIZE }
      // 第 1 頁不帶 until，確保輪詢時能取得最新資料
      if (this.until && this.currentPage > 1) {
        opts.until = this.until
        opts.page = this.currentPage
      }
      try {
        const result = await webAPI.getSwapTransactions(sourceToken, opts)
        if (result.res) {
          const data = result.res
          this.hasMore = data.has_more || false
          this.rows = (data.items || []).map(item => this.parseItem(item))
          if (this.currentPage === 1 && this.rows.length > 0 && data.items.length > 0) {
            this.until = data.items[0].CreatedAt
          }
        } else {
          console.error('getSwapTransactions error:', result.errMsg)
          this.rows = []
          this.hasMore = false
        }
      } finally {
        this.isLoading = false
      }
    },
    parseItem (item) {
      // 時間格式化
      const d = new Date(item.CreatedAt * 1000)
      const pad = n => String(n).padStart(2, '0')
      const time = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`

      // 狀態：只有「處理中」或「已完成」
      const done = item.Status === 1
      const statusText = done ? this.$t('message.status_done') : this.$t('message.status_pending')

      // 鏈別：統一顯示目標鏈
      const chainLabel = utils.getChainDisplay(item.TargetToken)

      // tx_hash 截斷
      let txHashShort = null
      if (item.TxHash) {
        const h = item.TxHash
        txHashShort = h.length > 12 ? `${h.slice(0, 6)}…${h.slice(-4)}` : h
      }

      return {
        time,
        address: item.TargetAddress,
        amount: this.formatAmount(item.Amount),
        done,
        status: statusText,
        chainLabel,
        txHash: item.TxHash,
        txHashShort,
        targetToken: item.TargetToken
      }
    },
    getTxLink (row) {
      // BEP-20/ERC-20 tab: target is COS mainnet, tx is on Contentos chain
      if (this.currentTab === 'bep20' || this.currentTab === 'erc20') {
        return utils.getTxExplorerLink('cos', row.txHash)
      }
      // COS tab: tx is on the target chain
      return utils.getTxExplorerLink(row.targetToken, row.txHash)
    },
    startCountdown () {
      this.stopCountdown()
      this.secondsUntilRefresh = this.refreshIntervalSec
      this.countdownTimer = setInterval(() => {
        if (this.isLoading) return
        if (this.secondsUntilRefresh <= 1) {
          this.secondsUntilRefresh = this.refreshIntervalSec
          this.fetchData()
          return
        }
        this.secondsUntilRefresh -= 1
      }, 1000)
    },
    stopCountdown () {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer)
        this.countdownTimer = null
      }
    }
  }
}
</script>

<style scoped>
.status_block { width: 100%; display: flex; flex-direction: column; gap: 12px; }
.source_network_label { font-size: 16px; font-weight: 600; color: #71717a; text-align: center; }
.network_tabs { width: 100%; display: flex; margin-bottom: 12px; }
.network_tab {
  flex: 1; height: 48px;
  position: relative;
  border: none;
  border-radius: 0;
  background: #f3f4f6;
  color: #71717a;
  font-size: 13px; font-weight: 600; cursor: pointer;
}
.network_tab::before {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 3px;
  background: #f3f4f6;
}
.network_tab.active {
  background: #fff;
  color: #6a11cb;
  font-weight: 700;
}
.network_tab.active::before {
  background: linear-gradient(90deg, #6a11cb 0%, #2575fc 100%);
}
.table_wrap { width: 100%; }
.native_table { width: 100%; border: 1px solid #e4e4e7; border-radius: 0; background: #fff; overflow: hidden; }
.table_head, .table_row { display: flex; align-items: center; gap: 16px; padding: 12px 16px; }
.table_head { background: #f9fafb; color: #71717a; font-size: 12px; font-weight: 600; }
.table_line { width: 100%; height: 1px; background: #e4e4e7; }
.table_row + .table_row { border-top: 1px solid #e4e4e7; }
.table_empty { padding: 28px 16px; text-align: center; color: #a1a1aa; font-size: 13px; }
.col_time { flex-shrink: 0; width: 120px; white-space: nowrap; }
.col_chain { flex-shrink: 0; width: 90px; }
.col_addr { flex: 1; min-width: 0; }
.col_amount { flex-shrink: 0; width: 90px; text-align: right; white-space: nowrap; }
.chain_badge { color: #71717a; font-size: 13px; font-weight: 400; }
.chain_badge_inline { display: none; font-weight: 700; }
.col_status { flex-shrink: 0; width: 220px; }
.row_muted { color: #71717a; font-size: 13px; font-weight: 400; }
.row_addr { color: #18181b; font-size: 13px; font-weight: 400; white-space: normal; overflow-wrap: anywhere; word-break: break-all; }
.status_row { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.status_pending { color: #2563eb; font-size: 13px; font-weight: 700; }
.status_done { color: #71717a; font-size: 13px; font-weight: 700; }
.status_hash_link { color: #71717a; font-size: 12px; font-weight: 500; text-decoration: underline; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pagination_bar { width: 100%; display: flex; flex-direction: column; gap: 6px; }
.page_hint { color: #71717a; font-size: 11px; font-weight: 500; }
.page_controls { width: 100%; display: flex; justify-content: center; gap: 8px; padding: 6px 0; }
.page_btn { width: 28px; height: 48px; border: 1px solid #e4e4e7; border-radius: 0; background: #fff; color: #71717a; font-size: 12px; font-weight: 600; cursor: pointer; }
.page_btn:disabled { opacity: 0.35; cursor: default; }

@media (max-width: 640px) {
  .table_head { display: none; }
  .table_row {
    display: grid;
    grid-template-columns: 1fr auto;
    grid-template-areas: "time amount" "addr addr" "status status";
    gap: 12px;
    padding: 12px 14px;
  }
  .col_time { grid-area: time; width: auto; min-width: 0; display: flex; align-items: center; gap: 6px; }
  .col_chain { display: none; }
  .col_addr { grid-area: addr; width: 100%; min-width: 0; font-size: 13px; }
  .col_amount { grid-area: amount; width: auto; min-width: 0; text-align: right; color: #18181b; font-size: 13px; font-weight: 700; white-space: nowrap; }
  .col_amount::after { content: " COS"; }
  .col_status { grid-area: status; width: auto; min-width: 0; }
  .chain_badge_inline { display: inline; }
}
</style>
